import React, { useMemo, cloneElement, isValidElement, ReactElement } from 'react';
import clsx from 'clsx';
import {
  EditableComponent,
  EditableComponentProps,
  ResponsiveGrid,
  getChildComponents,
} from '@adobe/aem-react-editable-components';
import { respGridUtil, useEditorMode } from '@marriott/mi-ui-library';
import { SixPackContainerProps, SixPackContainerCompProps } from './SixPackContainer.types';
import { SixPackContentContainer } from './SixPackContainer.styles';

const sortCqItemsImmutable = (page: SixPackContainerCompProps, variation: 'square-first' | 'tall-first') => {
  const cardsList = page?.[':items'] || {};
  const cardsOrder = page?.[':itemsOrder'] || [];

  const pattern = variation?.toLowerCase() === 'square-first' ? ['square', 'tall'] : ['tall', 'square'];

  let remaining = cardsOrder.map(key => ({
    key,
    variation: cardsList[key]?.cardVariation?.toLowerCase(),
  }));

  const sorted: string[] = [];
  for (let i = 0; i < cardsOrder.length; i++) {
    const expected = pattern[i % 2];
    const index = remaining.findIndex(item => item.variation === expected);

    if (index !== -1) {
      sorted.push(remaining[index].key);
      remaining = remaining.filter((_, j) => j !== index);
    } else {
      sorted.push(remaining.shift()?.key as string);
    }
  }
  return sorted;
};

const PublishedSixPackContentContainer = (model: SixPackContainerCompProps) => {
  const { variation } = model;
  const content = useMemo(() => {
    const cqModel = respGridUtil(model)?.cqItems?.container;
    const sortedItemOrder = sortCqItemsImmutable(cqModel, variation);
    const cqItems = cqModel?.[':items'];
    return getChildComponents({ cqItems, cqItemsOrder: sortedItemOrder });
  }, [model]);

  const componentWithViewPortProp =
    content &&
    content.map((item: ReactElement) => {
      if (item && isValidElement(item)) {
        return cloneElement(item as ReactElement, {
          breakPointKey: 'viewportL',
        });
      }
      return item as React.ReactNode;
    });

  return <div className="six-pack-wrapper">{componentWithViewPortProp}</div>;
};

export const SixPackContainerComp = (model: SixPackContainerCompProps) => {
  const { variation, styleclass = '', componentId, pagePath = '', itemPath = '', isAuthorMode } = model;
  const { isInEditor } = useEditorMode();

  return (
    <div
      className={clsx(styleclass)}
      data-testid="homepage-SixPackContainer"
      data-component-name="o-homepage-SixPackContainer"
      id={componentId}
    >
      <SixPackContentContainer variation={variation}>
        {isAuthorMode ? (
          <ResponsiveGrid
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            pagePath={pagePath}
            itemPath={`${itemPath}/container`}
            columnCount="12"
            gridClassNames=""
            customClassName="six-pack-wrapper"
            config={{
              isEmpty: () => true,
              resourceType: 'mi-aem-common-spa/components/container',
            }}
            isInEditor={isInEditor}
          />
        ) : (
          <PublishedSixPackContentContainer {...model} />
        )}
      </SixPackContentContainer>
    </div>
  );
};

const SixPackContainerConfig = {
  emptyLabel: 'SixPackContainer',
  isEmpty: () => true,
  resourceType: 'mi-aem-homepage-spa/components/content/sixpackcontainer',
};

export const SixPackContainer = (props: EditableComponentProps & SixPackContainerProps) => {
  return (
    <EditableComponent config={SixPackContainerConfig} {...props}>
      <SixPackContainerComp {...props.model} isAuthorMode={props.isAuthorMode} />
    </EditableComponent>
  );
};
